import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import {
    Booking_Status_Enum,
    Booking_Type_Enum,
    BookingFragment,
    Bookings_Insert_Input,
    Invoice_Target_Enum,
    Rate_Type_Enum,
    UpdateBookingMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminVenueBookingCreateFragment, 
    AgencyOperationsAdminVenueBookingCreateSchema, 
    AgencyOperationsAdminVenueBookingUpdateFragment, 
    AgencyOperationsAdminVenueBookingUpdateSchema 
} from "src/components/agency/operations/administrator/venues/bookings/AgencyOperationsAdminVenueBookingSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = AgencyOperationsAdminVenueBookingCreateFragment

let initialState: FormPayloadType = {
    venue_id: '',
    area_id: '',
    talent_id: '',
    date: '',
    event_start_time: '',
    event_end_time: '',
    set_start_time: '',
    set_end_time: '',
    notes: '',
    rate: '',
    rate_type: Rate_Type_Enum.Hourly,
    rate_margin: '',
    invoice_target: Invoice_Target_Enum.Venue,
    type: Booking_Type_Enum.VenueBooking,
    poc: '',
};

export const CreateAgencyOperationsAdminVenueBookingDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().bookings;
    const { loading, errors } = useAppState().bookings;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Bookings_Insert_Input) => {
        return createItem({ ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, BookingFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueBookingCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminVenueBookingAction = () => {
    return (
        <PageDrawerAction<BookingFragment>
            title='Add Booking'
            action={CreateAgencyOperationsAdminVenueBookingDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminVenueBookingUpdateFragment = {
    status: Booking_Status_Enum.Draft,
    venue_id: '',
    area_id: '',
    talent_id: '',
    date: '',
    event_start_time: '',
    event_end_time: '',
    set_start_time: '',
    set_end_time: '',
    notes: '',
    rate: '',
    rate_type: Rate_Type_Enum.Hourly,
    rate_margin: '',
    invoice_target: Invoice_Target_Enum.Venue,
    type: Booking_Type_Enum.VenueBooking,
    poc: '',
};

export const UpdateAgencyOperationsAdminVenueBookingDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().bookings;
    const { currentItem, loading, errors } = useAppState().bookings;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminVenueBookingUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminVenueBookingUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateBookingMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminVenueBookingUpdateFragment, BookingFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueBookingUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminVenueBookingAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().bookings;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.bookings.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<BookingFragment>
            title='Edit Booking'
            action={UpdateAgencyOperationsAdminVenueBookingDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminVenueBookingAction = () => {
    return (
        <PageDrawerAction<BookingFragment>
            title='Edit Booking'
            action={UpdateAgencyOperationsAdminVenueBookingDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}